import axios, { type AxiosError } from 'axios';

import type { ApiErrorType, ServerError } from 'models';

export interface ParseErrorOptions {
    isMaintenance?: boolean;
}

export const parseError = (error: AxiosError<ApiErrorType>, options?: ParseErrorOptions): ServerError => {
    const wrapper: ServerError = {
        unknown: false,
        invalid: false,
        notFound: false,
        unauthorized: false,
        forbidden: false,
        unavailable: false,
        internal: false,
        conflict: false,
        maintenance: options?.isMaintenance ?? false,
        tooManyRequests: false,
        timeout: error.code === 'ECONNABORTED',
        cancelled: axios.isCancel(error),
        error: {
            message: '',
            details: '',
            code: '',
            service: '',
            data: {},
        },
    };

    // error.response is undefined when the api is off
    if (error.response) {
        const code = error.response.status;

        if (code === 500) {
            wrapper.internal = true;
        } else if (code > 500) {
            wrapper.unavailable = true;
        } else if (code === 404) {
            wrapper.notFound = true;
        } else if (code === 401) {
            wrapper.unauthorized = true;
        } else if (code === 403) {
            wrapper.forbidden = true;
        } else if (code === 400) {
            wrapper.invalid = true;
        } else if (code === 409) {
            wrapper.conflict = true;
        } else if (code === 429) {
            wrapper.tooManyRequests = true;
        } else {
            wrapper.unknown = true;
        }

        if (!!error?.response?.data) {
            const data = error.response.data;
            if (!!data?.error) {
                // TODO(sumer.sao) these errors are handled this way due to BE inconsistency. This scheme will
                // be standardized after the API v2
                const rawError = data.error as ApiErrorType['error'] | string;
                if (typeof rawError === 'string') {
                    wrapper.error = {
                        code: rawError,
                        message: (data as { error_description?: string }).error_description ?? rawError,
                        details: '',
                        service: '',
                        data: {},
                    };
                } else {
                    wrapper.error = rawError;
                }
            }

            const { error: _, ...dataWithoutError } = data;
            wrapper.error.data = dataWithoutError;
        }
    } else if (error.request) {
        // if there was a request in the error
        // it means the client did the request but the server was unavailable
        wrapper.unavailable = true;
    } else {
        wrapper.unknown = true;
    }

    return wrapper;
};
